class DurynHelpCentre extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.pageKey = this.dataset.pageKey || 'help-centre';
    this.activeCategory = 'all';
    this.cacheElements();
    this.bindEvents();
    this.prefillSearch();
    this.applyFilters({ scroll: false });
    this.openHash();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  cacheElements() {
    const selectorKey = CSS.escape(this.pageKey);
    this.searchInput = document.querySelector(`[data-help-search][data-help-page="${selectorKey}"]`);
    this.searchHint = document.querySelector(`[data-help-hint][data-help-page="${selectorKey}"]`);
    this.featured = document.querySelector(`[data-help-featured][data-help-page="${selectorKey}"]`);
    this.categorySections = [...document.querySelectorAll(`[data-help-category-section][data-help-page="${selectorKey}"]`)];
    this.groupsRoot = this.categorySections[0];
    this.groups = this.categorySections.map((category) => category.querySelector('.hc-group')).filter(Boolean);
    this.questions = this.groups.flatMap((group) => [...group.querySelectorAll('.hc-q')]);
    this.chips = this.querySelector('[data-help-chips]');
    this.count = this.querySelector('[data-help-count]');
    this.empty = this.querySelector('[data-help-empty]');
    this.expand = this.querySelector('[data-help-expand]');

    this.questions.forEach((question) => {
      question.dataset.searchText = this.normalize(question.textContent);
    });
  }

  bindEvents() {
    this.searchInput?.addEventListener('input', () => this.applyFilters({ scroll: true }), { signal: this.signal });
    this.chips?.addEventListener('click', (event) => this.handleCategoryClick(event), { signal: this.signal });
    this.expand?.addEventListener('click', () => this.toggleAll(), { signal: this.signal });
    window.addEventListener('hashchange', () => this.openHash(), { signal: this.signal });
    window.addEventListener('beforeprint', () => this.openAll(), { signal: this.signal });
    document.addEventListener('shopify:section:load', () => this.refreshContent(), { signal: this.signal });
  }

  prefillSearch() {
    if (!this.searchInput || this.searchInput.value) return;
    const term = new URLSearchParams(window.location.search).get('q');
    if (term) this.searchInput.value = term;
  }

  handleCategoryClick(event) {
    const button = event.target.closest('[data-help-category]');
    if (!button || !this.chips.contains(button)) return;

    this.activeCategory = button.dataset.helpCategory;
    this.chips.querySelectorAll('[data-help-category]').forEach((chip) => {
      chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
    });

    this.applyFilters({ scroll: false });
    if (this.activeCategory !== 'all') {
      const group = this.groups.find((item) => item.dataset.c === this.activeCategory);
      group?.scrollIntoView({ block: 'start' });
    }
  }

  applyFilters({ scroll }) {
    const rawTerm = this.searchInput?.value.trim() || '';
    const term = this.normalize(rawTerm);
    let shown = 0;

    this.groups.forEach((group) => {
      const categoryMatches = this.activeCategory === 'all' || group.dataset.c === this.activeCategory;
      let groupCount = 0;

      group.querySelectorAll('.hc-q').forEach((question) => {
        const matches = categoryMatches && (!term || question.dataset.searchText.includes(term));
        question.hidden = !matches;
        if (matches) {
          groupCount += 1;
          if (term) question.open = true;
        }
      });

      group.hidden = groupCount === 0;
      const groupTotal = group.querySelector('[data-n]');
      if (groupTotal) groupTotal.textContent = this.formatQuestionCount(groupCount);
      shown += groupCount;
    });

    if (this.featured) this.featured.hidden = Boolean(term) || this.activeCategory !== 'all';
    if (this.empty) this.empty.hidden = shown !== 0;
    if (this.count) {
      this.count.textContent = term || this.activeCategory !== 'all'
        ? `${shown} ${this.dataset.ofLabel || ''} ${this.questions.length}`
        : `${this.questions.length} ${this.dataset.answersLabel || ''}`;
    }

    if (this.searchHint) {
      if (!rawTerm) {
        this.searchHint.textContent = '';
      } else if (shown) {
        this.searchHint.textContent = `${shown} ${shown === 1 ? this.dataset.answerLabel || '' : this.dataset.answersLabel || ''} ${this.dataset.forLabel || ''} “${rawTerm}”`;
      } else {
        this.searchHint.textContent = `${this.dataset.noAnswersLabel || ''} “${rawTerm}”`;
      }
    }

    if (scroll && term && this.groupsRoot?.getBoundingClientRect().top > window.innerHeight * 0.6) {
      this.groupsRoot.scrollIntoView({ block: 'start' });
    }
  }

  formatQuestionCount(count) {
    const label = count === 1 ? this.dataset.questionLabel || '' : this.dataset.questionsLabel || '';
    return `${count} ${label}`;
  }

  toggleAll() {
    const shouldOpen = this.expand.getAttribute('aria-pressed') !== 'true';
    this.questions.filter((question) => !question.hidden).forEach((question) => {
      question.open = shouldOpen;
    });
    this.expand.setAttribute('aria-pressed', shouldOpen ? 'true' : 'false');
    this.expand.textContent = shouldOpen
      ? this.dataset.collapseLabel || ''
      : this.dataset.expandLabel || '';
  }

  openAll() {
    this.questions.filter((question) => !question.hidden).forEach((question) => {
      question.open = true;
    });
  }

  openHash() {
    if (!window.location.hash.startsWith('#q-')) return;
    const target = document.getElementById(window.location.hash.slice(1));
    if (!target?.classList.contains('hc-q') || !this.questions.includes(target)) return;

    const group = target.closest('.hc-group');
    if (target.hidden || group?.hidden) {
      if (this.searchInput) this.searchInput.value = '';
      this.activeCategory = 'all';
      this.chips?.querySelectorAll('[data-help-category]').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.helpCategory === 'all' ? 'true' : 'false');
      });
      this.applyFilters({ scroll: false });
    }

    target.open = true;
  }

  normalize(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase();
  }

  refreshContent() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.cacheElements();
    this.bindEvents();
    this.applyFilters({ scroll: false });
    this.openHash();
  }
}

if (!customElements.get('duryn-help-centre')) {
  customElements.define('duryn-help-centre', DurynHelpCentre);
}
